import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import "./touchGfxDisplay.css";

export const DISPLAY_WIDTH = 480;
export const DISPLAY_HEIGHT = 272;
const PIXEL_BYTES = DISPLAY_WIDTH * DISPLAY_HEIGHT * 3;
const MAX_SEQUENCE = 2147483647;
const PRIVATE_BRIDGE = "http://127.0.0.1:4181";
const REQUEST_TIMEOUT = 5000;
const FRAME_FRESHNESS = 5000;

class SimulatorHttpError extends Error {
  constructor(status, operation) {
    super("SIMULATOR_HTTP_ERROR");
    this.status = status; this.operation = operation;
  }
}

function simulatorErrorMessage(error, fallback) {
  // Never display response bodies or a network exception's arbitrary message.
  if (!(error instanceof SimulatorHttpError)) return fallback;
  if (error.status === 409 && error.operation === "session") return "Die Simulator-Kapazität ist derzeit belegt. Bitte später erneut verbinden.";
  if (error.status === 410) return "Diese Simulator-Sitzung ist beendet. Bitte bei Bedarf neu verbinden.";
  if (error.status === 429) return "Zu viele Anfragen an den Simulator. Bitte kurz warten und dann erneut verbinden.";
  if (error.status === 503) return "Der Simulator ist derzeit nicht verfügbar. Bitte später erneut verbinden. Es werden keine Ersatzbilder angezeigt.";
  return fallback;
}

// This local preview opt-in never accepts a URL from a query string or a visitor.
export function resolveTouchGfxBridge(value) {
  return typeof value === "string" && /^http:\/\/127\.0\.0\.1:4181\/?$/.test(value) ? PRIVATE_BRIDGE : null;
}

export function displayCoordinates(event, rect) {
  if (!rect || ![rect.left, rect.top, rect.width, rect.height, event.clientX, event.clientY].every(Number.isFinite) || rect.width <= 0 || rect.height <= 0) return null;
  // Account for a possible letterbox as well as CSS scaling. Outside presses are
  // not silently moved onto an edge button in the original GUI.
  const scale = Math.min(rect.width / DISPLAY_WIDTH, rect.height / DISPLAY_HEIGHT);
  const width = DISPLAY_WIDTH * scale, height = DISPLAY_HEIGHT * scale;
  const x = event.clientX - rect.left - (rect.width - width) / 2;
  const y = event.clientY - rect.top - (rect.height - height) / 2;
  if (x < 0 || y < 0 || x >= width || y >= height) return null;
  return { x: Math.min(DISPLAY_WIDTH - 1, Math.floor(x / scale)), y: Math.min(DISPLAY_HEIGHT - 1, Math.floor(y / scale)) };
}

export function decodeTouchGfxFrame(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength !== PIXEL_BYTES + 8) throw new Error("INVALID_FRAME");
  const header = new DataView(buffer);
  const sequence = header.getUint32(4, false);
  if (header.getUint32(0, false) !== 0 || sequence < 1 || sequence > MAX_SEQUENCE) throw new Error("INVALID_FRAME");
  return { sequence, pixels: new Uint8Array(buffer, 8) };
}

async function readBoundedBody(response, limit) {
  if (!response.body?.getReader) {
    const buffer = await response.arrayBuffer();
    if (buffer.byteLength > limit) throw new Error("RESPONSE_TOO_LARGE");
    return buffer;
  }
  const reader = response.body.getReader(), bytes = new Uint8Array(limit);
  let length = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value.byteLength > limit - length) { await reader.cancel(); throw new Error("RESPONSE_TOO_LARGE"); }
      bytes.set(value, length); length += value.byteLength;
    }
    return length === limit ? bytes.buffer : bytes.buffer.slice(0, length);
  } finally { reader.releaseLock(); }
}

async function request(run, path, options = {}, frameRequest = false) {
  const controller = new AbortController();
  if (frameRequest) run.frameAbort = controller;
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  try {
    const response = await run.fetcher(`${run.url}/__touchgfx/${path}`, {
      ...options, signal: controller.signal, credentials: "omit", cache: "no-store",
      redirect: "error", referrerPolicy: "no-referrer",
      headers: { ...(options.headers || {}), ...(run.token ? { "X-TouchGFX-Session": run.token } : {}) }
    });
    if (response.ok && frameRequest && response.status !== 204) return { ok: true, status: response.status, buffer: await readBoundedBody(response, PIXEL_BYTES + 8) };
    if (response.ok && path === "session" && options.method === "POST") {
      const buffer = await readBoundedBody(response, 1024);
      const text = String.fromCharCode(...new Uint8Array(buffer));
      return { ok: true, status: response.status, data: JSON.parse(text) };
    }
    return response;
  } finally {
    clearTimeout(timeout);
    if (frameRequest && run.frameAbort === controller) run.frameAbort = null;
  }
}

async function sendPointer(run, kind, position) {
  if (run.sequence >= MAX_SEQUENCE) throw new Error("SEQUENCE_EXHAUSTED");
  const body = JSON.stringify({ schema: "feeder.touchgfx.pointer.v1", seq: ++run.sequence, kind, x: position.x, y: position.y });
  if (body.length > 256) throw new Error("INVALID_POINTER");
  const response = await request(run, "pointer", { method: "POST", headers: { "Content-Type": "application/json" }, body });
  if (!response.ok) throw new SimulatorHttpError(response.status, "pointer");
}

// One in-flight input, one coalesced move and one release. A fast pointer cannot
// create an unbounded request queue, and a new gesture waits for the prior up.
function dispatchPointer(run, kind, position, fail) {
  run.posting = sendPointer(run, kind, position)
    .then(() => { if (kind === "up") run.wasDown = false; })
    .catch(error => { if (!run.closing) fail(run, simulatorErrorMessage(error, "Die Simulator-Eingabe wurde unterbrochen. Bitte neu verbinden.")); })
    .finally(() => {
      run.posting = null;
      if (run.closing) return;
      const up = run.pendingUp, move = run.pendingMove;
      run.pendingUp = null; run.pendingMove = null;
      if (up) dispatchPointer(run, "up", up, fail);
      else if (move) dispatchPointer(run, "move", move, fail);
    });
}

function releaseCapture(run) {
  const held = run.held;
  run.held = null;
  if (held?.target?.hasPointerCapture?.(held.id)) held.target.releasePointerCapture(held.id);
}

function stopSession(run) {
  if (!run) return Promise.resolve();
  if (run.stopping) return run.stopping;
  run.closing = true; run.ready = false;
  clearTimeout(run.timer); clearTimeout(run.freshnessTimer); run.frameAbort?.abort();
  run.pendingMove = null; run.pendingUp = null;
  releaseCapture(run);
  run.stopping = (async () => {
    // A late session creation still gets deleted instead of leaking a simulator.
    await run.creating?.catch(() => {});
    await run.posting?.catch(() => {});
    if (!run.token) return;
    if (run.wasDown && run.last) {
      try { await sendPointer(run, "up", run.last); } catch (_) { /* Server disconnect must also release. */ }
    }
    run.wasDown = false;
    try { await request(run, "session", { method: "DELETE" }); } catch (_) { /* Bounded best effort; server needs an independent expiry. */ }
    run.token = null;
  })();
  return run.stopping;
}

const TouchGfxDisplay = forwardRef(function TouchGfxDisplay({ showLauncher = true, bridgeUrl = process.env.REACT_APP_TOUCHGFX_BRIDGE_URL, onFrame, onStatusChange }, ref) {
  const [open, setOpen] = useState(false), [status, setStatus] = useState("closed"), [message, setMessage] = useState("");
  const active = useRef(null), canvas = useRef(null), mounted = useRef(true), cleanup = useRef(Promise.resolve());
  const observers = useRef({ onFrame, onStatusChange });
  observers.current = { onFrame, onStatusChange };
  const url = resolveTouchGfxBridge(bridgeUrl);
  // A presentation observer must never interrupt pointer release or cleanup.
  const notify = useCallback((name, value) => {
    try { observers.current[name]?.(value); } catch (_) { /* Isolate the optional view. */ }
  }, []);

  useEffect(() => { notify("onStatusChange", status); }, [notify, status]);

  const trackStop = run => {
    if (run) cleanup.current = Promise.all([cleanup.current, stopSession(run)]).then(() => {});
    return cleanup.current;
  };

  const clearDisplay = () => {
    const context = canvas.current?.getContext("2d");
    context?.clearRect(0, 0, DISPLAY_WIDTH, DISPLAY_HEIGHT);
    notify("onFrame", null);
  };
  const fail = (run, text) => {
    if (active.current !== run || !mounted.current) return;
    active.current = null;
    setStatus("error"); setMessage(text); clearDisplay();
    void trackStop(run);
  };
  const armFreshness = run => {
    clearTimeout(run.freshnessTimer);
    run.lastFrameAt = Date.now();
    run.freshnessTimer = setTimeout(() => fail(run, "Seit fünf Sekunden kommt kein neues Originalbild an. Die Anzeige wurde geleert. Bitte neu verbinden."), FRAME_FRESHNESS);
  };

  useEffect(() => {
    mounted.current = true;
    const blur = () => {
      const run = active.current;
      if (run?.wasDown) fail(run, "Die Berührung wurde abgebrochen. Bitte neu verbinden.");
    };
    window.addEventListener("blur", blur);
    return () => {
      mounted.current = false; window.removeEventListener("blur", blur);
      const run = active.current; active.current = null; void trackStop(run);
      clearDisplay(); notify("onStatusChange", "closed");
    };
    // Session cleanup owns its own immutable endpoint and token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = () => {
    if (!mounted.current) return;
    // A ref guard also covers two start calls before React commits Connecting,
    // including the period spent awaiting cleanup of the previous session.
    if (active.current && !active.current.ready && !active.current.closing) return;
    const previous = active.current; active.current = null; const stopped = trackStop(previous);
    setOpen(true); clearDisplay();
    if (!url) {
      setStatus("unavailable"); setMessage("Das Originaldisplay ist hier noch nicht verfügbar. Für eine private Vorschau muss die lokale Simulator-Verbindung ausdrücklich konfiguriert sein."); return;
    }
    setStatus("connecting"); setMessage("Eigene private Simulator-Sitzung wird geöffnet · frischer Zustand …");
    // request() calls this as a run method; keep native fetch's Window receiver.
    const run = { url, fetcher: fetch.bind(window), token: null, sequence: 0, frameSequence: 0, ready: false, closing: false, held: null, wasDown: false, posting: null, pendingMove: null, pendingUp: null };
    active.current = run;
    const poll = async () => {
      if (run.closing || active.current !== run) return;
      try {
        const response = await request(run, "frame", { method: "GET" }, true);
        if (run.closing || active.current !== run) return;
        if (response.status !== 204) {
          if (!response.ok) throw new SimulatorHttpError(response.status, "frame");
          const frame = decodeTouchGfxFrame(response.buffer);
          if (frame.sequence < run.frameSequence) throw new Error("STALE_FRAME");
          if (frame.sequence > run.frameSequence) {
            const context = canvas.current?.getContext("2d");
            if (!context) throw new Error("CANVAS_UNAVAILABLE");
            const image = context.createImageData(DISPLAY_WIDTH, DISPLAY_HEIGHT);
            for (let input = 0, output = 0; input < PIXEL_BYTES; input += 3, output += 4) {
              image.data[output] = frame.pixels[input]; image.data[output + 1] = frame.pixels[input + 1]; image.data[output + 2] = frame.pixels[input + 2]; image.data[output + 3] = 255;
            }
            context.putImageData(image, 0, 0); run.frameSequence = frame.sequence; run.ready = true;
            armFreshness(run);
            setStatus("ready"); setMessage("Originaldisplay verbunden · nur simulierte Gerätewerte");
            // The optional 3D view owns its RGB-only copy, never the transport
            // buffer or a mutable canvas backing store.
            notify("onFrame", { width: DISPLAY_WIDTH, height: DISPLAY_HEIGHT, pixels: frame.pixels.slice(), sequence: frame.sequence });
          }
        }
        if (!run.closing && active.current === run) run.timer = setTimeout(poll, 200);
      } catch (error) { if (!run.closing) fail(run, simulatorErrorMessage(error, "Es kommen keine gültigen Originalbilder an. Bitte neu verbinden.")); }
    };
    run.creating = (async () => {
      // Never race a previous DELETE, including cleanup started by an error.
      await stopped;
      if (run.closing || active.current !== run || !mounted.current) return;
      armFreshness(run);
      const response = await request(run, "session", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      if (!response.ok) throw new SimulatorHttpError(response.status, "session");
      const data = response.data;
      if (!data || typeof data.token !== "string" || !/^[A-Za-z0-9._~-]{1,256}$/.test(data.token)) throw new Error("INVALID_SESSION");
      run.token = data.token;
      if (data.width !== DISPLAY_WIDTH || data.height !== DISPLAY_HEIGHT) throw new Error("INVALID_DISPLAY_SIZE");
    })();
    run.creating.then(() => { if (!run.closing && active.current === run) void poll(); })
      .catch(error => { if (!run.closing) fail(run, simulatorErrorMessage(error, "Die private Simulator-Sitzung konnte nicht geöffnet werden. Es werden keine Ersatzbilder angezeigt.")); });
  };

  const close = () => {
    const run = active.current; active.current = null; void trackStop(run);
    clearDisplay(); setOpen(false); setStatus("closed");
  };
  const position = event => displayCoordinates(event, event.currentTarget.getBoundingClientRect());
  const acceptsInput = run => {
    if (!run?.ready || run.closing) return false;
    if (Date.now() - run.lastFrameAt >= FRAME_FRESHNESS) {
      fail(run, "Seit fünf Sekunden kommt kein neues Originalbild an. Die Anzeige wurde geleert. Bitte neu verbinden."); return false;
    }
    return true;
  };
  const down = event => {
    const run = active.current;
    if (!acceptsInput(run) || run.wasDown || run.posting || event.button !== 0 || event.isPrimary === false) return;
    const point = position(event); if (!point) return;
    event.preventDefault(); run.last = point; run.wasDown = true;
    run.held = { source: "canvas", id: event.pointerId, target: event.currentTarget };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dispatchPointer(run, "down", point, fail);
  };
  const move = event => {
    const run = active.current;
    if (!acceptsInput(run) || run.held?.source !== "canvas" || run.held.id !== event.pointerId) return;
    const point = position(event); if (!point) return;
    event.preventDefault(); run.last = point;
    if (run.posting) run.pendingMove = point; else dispatchPointer(run, "move", point, fail);
  };
  const up = event => {
    const run = active.current;
    if (!acceptsInput(run) || run.held?.source !== "canvas" || run.held.id !== event.pointerId) return;
    event.preventDefault(); const point = position(event) || run.last; run.last = point;
    releaseCapture(run); run.pendingMove = null;
    if (run.posting) run.pendingUp = point; else dispatchPointer(run, "up", point, fail);
  };
  const cancel = event => {
    const run = active.current;
    if (run?.held?.source === "canvas" && run.held.id === event.pointerId) fail(run, "Die Berührung wurde abgebrochen. Bitte neu verbinden.");
  };

  const pointer = input => {
    if (!input || typeof input !== "object" || Array.isArray(input)
        || Object.keys(input).some(key => !["kind", "x", "y"].includes(key))
        || !["down", "move", "up", "cancel"].includes(input.kind)) return false;
    const validPosition = Number.isInteger(input.x) && Number.isInteger(input.y)
      && input.x >= 0 && input.x < DISPLAY_WIDTH && input.y >= 0 && input.y < DISPLAY_HEIGHT;
    // Cancellation does not invent a new position. If supplied, it must still
    // obey the native bounds; stopSession releases at the last accepted point.
    if (!validPosition && !(input.kind === "cancel" && input.x === undefined && input.y === undefined)) return false;
    const run = active.current;
    if (!run || run.closing || !mounted.current) return false;
    if (input.kind === "cancel") {
      fail(run, "Die Berührung wurde abgebrochen. Bitte neu verbinden."); return true;
    }
    if (!acceptsInput(run)) return false;
    const point = { x: input.x, y: input.y };
    if (input.kind === "down") {
      if (run.wasDown || run.posting) return false;
      run.last = point; run.wasDown = true; run.held = { source: "3d" };
      dispatchPointer(run, "down", point, fail); return true;
    }
    if (!run.wasDown || run.held?.source !== "3d") return false;
    run.last = point;
    if (input.kind === "up") {
      releaseCapture(run); run.pendingMove = null;
      if (run.posting) run.pendingUp = point; else dispatchPointer(run, "up", point, fail);
    } else if (run.posting) run.pendingMove = point;
    else dispatchPointer(run, "move", point, fail);
    return true;
  };
  useImperativeHandle(ref, () => ({ start, close, pointer }));

  if (!open) return showLauncher ? <div className="feeder-touchgfx-launch"><button type="button" onClick={start}>Originaldisplay öffnen</button><small>Eigene, getrennte Simulator-Sitzung auf Klick. Keine echte Steuerung.</small></div> : null;
  return <section className="feeder-touchgfx" aria-label="Original-TouchGFX-Display">
    <div className="feeder-touchgfx-heading"><div><p>Original-TouchGFX-Simulator</p><h3>Das originale Menü ausprobieren</h3></div><button type="button" onClick={close}>Originaldisplay schließen</button></div>
    <p>480 × 272 Originalpixel aus einer privaten Linux-Simulation, auch auf das 3D-Display spiegelbar. Auf Klick startet eine eigene, getrennte Simulator-Sitzung mit frischem Zustand; beim Schließen endet diese Vorschau. Nur simulierte Gerätewerte, keine Hardwareverbindung. Das Lernlabor steuert dieses Display nicht; direkte Displayberührungen bedienen ausschließlich diese Simulator-Sitzung.</p>
    <p className={`feeder-touchgfx-status is-${status}`} role="status">{message}</p>
    <div className="feeder-touchgfx-screen" hidden={status === "unavailable"}>
      <canvas ref={canvas} width={DISPLAY_WIDTH} height={DISPLAY_HEIGHT} role="img" aria-label="Originaldisplay mit 480 mal 272 Bildpunkten; per Maus oder Berührung bedienbar" data-input-disabled={status !== "ready"} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel} onLostPointerCapture={cancel} onContextMenu={event => event.preventDefault()} />
      {status !== "ready" && <span className="feeder-touchgfx-placeholder">Noch kein Originalbild verfügbar</span>}
    </div>
    <p className="feeder-touchgfx-note">Nur einzelne Berührungen werden an den Simulator übertragen. Keine Tastatur-, Datei- oder Gerätebefehle. Die Bildanzeige ersetzt keine barrierefreie Gerätebedienung oder Sicherheitsprüfung.</p>
    {status === "error" && <button type="button" onClick={start}>Erneut verbinden</button>}
  </section>;
});

export default TouchGfxDisplay;
